import type { ReactElement } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import AppLayout from '../layout/AppLayout'
import RequireAuth from '../../auth/RequireAuth'
import AccessGate from '../../auth/AccessGate'
import AssistantPersonaBootstrap from '../../features/proxy/AssistantPersonaBootstrap'
import RequireGrant from '../../auth/RequireGrant'
import { ACTION_LIST_ALL_SCOPE_GRANTS, ACTION_READ_ANY_GRANTS, GRANTS } from '../../auth/grants'
import type { GrantCode } from '../../auth/grants'
import CallbackPage from '../../auth/CallbackPage'
import HomePage from '../../pages/HomePage'
import PlaceholderPage from '../../pages/PlaceholderPage'
import NotFoundPage from '../../pages/NotFoundPage'
import TicketRequestListPage from '../../features/tickets/TicketRequestListPage'
import TicketRequestNewPage from '../../features/tickets/TicketRequestNewPage'
import TicketRequestDetailPage from '../../features/tickets/TicketRequestDetailPage'
import TicketTypesAdminPage from '../../features/admin/TicketTypesAdminPage'
import RejectReasonsAdminPage from '../../features/admin/RejectReasonsAdminPage'
import UsersAdminPage from '../../features/admin/UsersAdminPage'
import GroupsAdminPage from '../../features/admin/GroupsAdminPage'
import RolesAdminPage from '../../features/admin/RolesAdminPage'
import GrantsAdminPage from '../../features/admin/GrantsAdminPage'
import SystemParametersAdminPage from '../../features/admin/SystemParametersAdminPage'
import ReferansAdminPage from '../../features/admin/ReferansAdminPage'
import OperationCodesAdminPage from '../../features/admin/OperationCodesAdminPage'
import NotificationTemplatesAdminPage from '../../features/admin/NotificationTemplatesAdminPage'
import TemplateParametersAdminPage from '../../features/admin/TemplateParametersAdminPage'
import SentEmailsAdminPage from '../../features/admin/SentEmailsAdminPage'
import UserProxiesAdminPage from '../../features/admin/UserProxiesAdminPage'
import StakeholdersPage from '../../features/activity/StakeholdersPage'
import EventTypesPage from '../../features/activity/EventTypesPage'
import ActivityListPage from '../../features/activity/ActivityListPage'
import ActivityFormPage from '../../features/activity/ActivityFormPage'
import ExecutiveSummaryPage from '../../features/activity/ExecutiveSummaryPage'
import InvitationTypesPage from '../../features/activity/InvitationTypesPage'
import CountriesPage from '../../features/location/CountriesPage'
import CriteriaScoreRangesPage from '../../features/activity/CriteriaScoreRangesPage'
import CriteriaScoresPage from '../../features/activity/CriteriaScoresPage'
import MeetingDecisionTypesPage from '../../features/activity/MeetingDecisionTypesPage'
import MeetingTypesPage from '../../features/meetingTypes/MeetingTypesPage'
import HolidayDaysPage from '../../features/holidayDays/HolidayDaysPage'
import ManagerListsPage from '../../features/managerLists/ManagerListsPage'
import TaskClassificationLookupPage from '../../features/taskClassifications/TaskClassificationLookupPage'
import TimePortfolioPage from '../../features/timePortfolio/TimePortfolioPage'
import TimePortfolioReportPage from '../../features/timePortfolio/TimePortfolioReportPage'
import MeetingsPage from '../../features/meetings/MeetingsPage'
import DraftMeetingsPage from '../../features/meetings/DraftMeetingsPage'
import GroupMeetingsPage from '../../features/meetings/GroupMeetingsPage'
import MeetingDetailPage from '../../features/meetings/MeetingDetailPage'
import MinutesArchivedMeetingsPage from '../../features/meetings/MinutesArchivedMeetingsPage'
import ActionsPage from '../../features/actions/ActionsPage'
import MeetingManagementPage from '../../features/meetingManagement/MeetingManagementPage'
import ActionsIndexRedirect from '../../features/actions/ActionsIndexRedirect'
import ActionDetailPage from '../../features/actions/ActionDetailPage'
import SubActionDetailPage from '../../features/actions/SubActionDetailPage'
import MinutesArchivedActionsPage from '../../features/actions/MinutesArchivedActionsPage'
import PrincipleDecisionsPage from '../../features/principleDecisions/PrincipleDecisionsPage'
import TodoListDetailPage from '../../features/todo/TodoListDetailPage'
import TodoListsPage from '../../features/todo/TodoListsPage'
import AgendaPage from '../../features/agenda/AgendaPage'
import AgendaDetailPage from '../../features/agenda/AgendaDetailPage'
import DeadlineRequestsPage from '../../features/deadline-requests/DeadlineRequestsPage'
import ApprovalsPage from '../../features/approvals/ApprovalsPage'
import MeetingGroupsPage from '../../features/meetings/MeetingGroupsPage'
import MinutesCreatePage from '../../features/minutes/MinutesCreatePage'
import PrefixesPage from '../../features/prefixes/PrefixesPage'
import NotificationsPage from '../../features/notifications/NotificationsPage'
import { MODULE_ITEMS } from '../nav/menu'
import { MENU_VIEW_GRANTS, withMenuGrant } from './menuGuard'

// Gerçeklenmiş ekranlar (diğer rotalar placeholder). Anahtar = menu.ts path.
const IMPLEMENTED: Record<string, ReactElement> = {
  '/tickets/list': <TicketRequestListPage />,
  '/tickets/new': <TicketRequestNewPage />,
  '/tickets/ticket-types': <TicketTypesAdminPage />,
  '/tickets/reject-reasons': <RejectReasonsAdminPage />,
  '/admin/users': <UsersAdminPage />,
  '/admin/groups': <GroupsAdminPage />,
  '/admin/roles': <RolesAdminPage />,
  '/admin/grants': <GrantsAdminPage />,
  '/admin/system-parameters': <SystemParametersAdminPage />,
  '/admin/referans': <ReferansAdminPage />,
  '/admin/operation-codes': <OperationCodesAdminPage />,
  '/admin/notification-templates': <NotificationTemplatesAdminPage />,
  '/admin/template-parameters': <TemplateParametersAdminPage />,
  // 078: Salt-okunur ekran ama kişisel veri (alıcı adresleri, bildirim gövdeleri) gösterir →
  // rota da menü gibi yetkiye bağlıdır; doğrudan URL ile girilemez.
  '/admin/sent-emails': (
    <RequireGrant anyOf={[GRANTS.SENT_EMAIL_VIEW]}>
      <SentEmailsAdminPage />
    </RequireGrant>
  ),
  // 090: Ekran yetki devrinin haritasını gösterir ve tek anahtara bağlıdır → rota da menü gibi
  // yetkiye bağlanır; doğrudan URL ile girilemez (uçlar zaten 403 döner, bu UX katmanı).
  '/admin/user-proxies': (
    <RequireGrant anyOf={[GRANTS.USER_PROXY_MANAGE]}>
      <UserProxiesAdminPage />
    </RequireGrant>
  ),
  '/events/list': <ActivityListPage />,
  '/events/new': <ActivityFormPage />,
  '/events/stakeholders': <StakeholdersPage />,
  '/events/event-types': <EventTypesPage />,
  '/events/invitation-types': <InvitationTypesPage />,
  '/events/countries': <CountriesPage />,
  '/events/criteria-score-ranges': <CriteriaScoreRangesPage />,
  '/events/criteria-scores': <CriteriaScoresPage />,
  '/admin/parameters/meeting-decision-types': <MeetingDecisionTypesPage />,
  '/admin/parameters/meeting-types': <MeetingTypesPage />,
  '/admin/parameters/holiday-days': <HolidayDaysPage />,
  '/admin/parameters/manager-lists': <ManagerListsPage />,
  // 127: iki katalog tek bileşen — `key` rotalar arası geçişte ekran durumunun taşınmasını önler.
  '/admin/parameters/strategic-objectives': (
    <TaskClassificationLookupPage key="strategicObjectives" kind="strategicObjectives" />
  ),
  '/admin/parameters/contributions': (
    <TaskClassificationLookupPage key="contributions" kind="contributions" />
  ),
  '/time-portfolio/entry': <TimePortfolioPage />,
  '/time-portfolio/report': <TimePortfolioReportPage />,
  '/minutes/meeting-minutes/meetings': <MeetingsPage />,
  // Aksiyonlar grup kökü — kendi ekranı yok; ilk yetkili alt sayfaya yönlendirir (detay "Listeye Dön"
  // fallback'i buraya düşer). Dört kapsam ayrı alt sayfadır (sol menüde "Aksiyonlar" başlığı altında).
  '/minutes/actions': (
    <RequireGrant
      anyOf={[
        GRANTS.ACTION_MENU_VIEW,
        ...ACTION_LIST_ALL_SCOPE_GRANTS,
        GRANTS.ACTION_LIST_OWN_VIEW,
        GRANTS.ACTION_LIST_ASSIGNED_VIEW,
        GRANTS.ACTION_LIST_FOLLOWED_VIEW,
      ]}
    >
      <ActionsIndexRedirect />
    </RequireGrant>
  ),
  /*
   * 105 — Toplantı Yönetimi. Menü kaydı, yolu, ikonu ve yetkisi (MEETING_MANAGEMENT_VIEW) 2026-09-16'da
   * ayrı bir işle eklenmişti ve bu rota IMPLEMENTED'da olmadığı için otomatik PlaceholderPage
   * gösteriyordu; ekran gerçeklendiği için artık gerçek sayfaya bağlanır.
   *
   * Kilit burada TEKRARLANMAZ: yol menüde olduğu için `withMenuGrant` korumayı menüden türetir
   * (menuGuard ilkesi). Elle bir RequireGrant eklemek, menüdeki yetkiyle sessizce ayrışabilecek
   * ikinci bir liste doğururdu.
   */
  '/minutes/actions/meeting-management': <MeetingManagementPage />,
  '/minutes/actions/all': (
    <RequireGrant anyOf={[...ACTION_LIST_ALL_SCOPE_GRANTS]}>
      <ActionsPage scope="all" />
    </RequireGrant>
  ),
  '/minutes/actions/created': (
    <RequireGrant anyOf={[GRANTS.ACTION_LIST_OWN_VIEW]}>
      <ActionsPage scope="created" />
    </RequireGrant>
  ),
  '/minutes/actions/assigned': (
    <RequireGrant anyOf={[GRANTS.ACTION_LIST_ASSIGNED_VIEW]}>
      <ActionsPage scope="assigned" />
    </RequireGrant>
  ),
  '/minutes/actions/followed': (
    <RequireGrant anyOf={[GRANTS.ACTION_LIST_FOLLOWED_VIEW]}>
      <ActionsPage scope="followed" />
    </RequireGrant>
  ),
  /*
   * Delegasyon (2026-08-30) — Anasayfa'daki "Delegasyon Özeti" kartının "Tümünü Gör" hedefi: kartın İKİ
   * dilimini (Bana Atanan + Devrettiklerim) TEK listede gösterir.
   *
   * MENÜDE YOKTUR (kullanıcı kararı: "şimdilik"): `menu.ts`'e eklenmedi, yalnız karttan ve doğrudan
   * adresle açılır. Rota koruması yine de var — menüde olmamak erişimi kısıtlamaz.
   *
   * Yetki İKİ koddan biri: sayfa yalnız KENDİ verinizi gösterir (size atanan / sizin devrettiğiniz),
   * dolayısıyla iki yarıdan birini görebilen tamamını görebilir. Yönetici'de ACTION_LIST_ASSIGNED_VIEW
   * var — "Oluşturduklarım"ı kaybedince erişilemez hâle gelen devir listesi böylece geri gelir.
   */
  '/minutes/actions/delegation': (
    <RequireGrant anyOf={[GRANTS.ACTION_LIST_ASSIGNED_VIEW, GRANTS.ACTION_LIST_OWN_VIEW]}>
      <ActionsPage scope="delegation" />
    </RequireGrant>
  ),
  // 087 — Termin güncelleme talepleri. Koruma, ucun HAS_ACTION_READ_ANY composite'ini birebir aynalar
  // (menü öğesiyle AYNI liste — tek kaynak: ACTION_READ_ANY_GRANTS).
  '/minutes/actions/deadline-requests': (
    <RequireGrant anyOf={[...ACTION_READ_ANY_GRANTS]}>
      <DeadlineRequestsPage />
    </RequireGrant>
  ),
  // 131 — Onaylarım. Kilit menüden türer (withMenuGrant): menü öğesinin viewGrants'ı (MY_APPROVALS_VIEW).
  // Hangi sekmenin görüneceğine sayfa, sekmelerin kendi yetkileriyle karar verir.
  '/minutes/actions/approvals': <ApprovalsPage />,
  '/minutes/principle-decisions': (
    <RequireGrant anyOf={[GRANTS.PRINCIPLE_DECISION_VIEW]}>
      <PrincipleDecisionsPage />
    </RequireGrant>
  ),
  '/minutes/todo-lists': (
    <RequireGrant anyOf={[GRANTS.TODO_LIST_VIEW]}>
      <TodoListsPage />
    </RequireGrant>
  ),
  // Ajanda (125) 15.10.2026'dan beri AGENDA_VIEW ister: menüdeki viewGrants'tan türeyen kilit burada elle
  // tekrarlanmaz (menu.ts). Detay rotası bilinçli olarak kilitsizdir — yetkisiz davetli de davetini açar.
  '/minutes/agenda': <AgendaPage />,
  '/minutes/archived/meetings': (
    <RequireGrant anyOf={[GRANTS.MEETING_ARCHIVE_VIEW]}>
      <MinutesArchivedMeetingsPage />
    </RequireGrant>
  ),
  '/minutes/archived/actions': (
    <RequireGrant anyOf={[GRANTS.ACTION_ARCHIVE_VIEW]}>
      <MinutesArchivedActionsPage />
    </RequireGrant>
  ),
  // 2026-09-16: Parametreler grubu Admin modülüne taşındı → yollar da `/admin/parameters/*`.
  // Eski `/minutes/*` yolları aşağıda (MOVED) kalıcı yönlendirme olarak korunur.
  '/admin/parameters/meeting-groups': <MeetingGroupsPage />,
  '/minutes/meeting-minutes/create': <MinutesCreatePage />,
  '/admin/parameters/prefixes': <PrefixesPage />,
  // Bildirimler (058): rota US1'de kaydedilir — panelin "Tümünü Gör" bağlantısı MVP'de çalışsın
  // diye (spec FR-016). Sayfanın içeriği (liste/filtre/sayfalama) US3'te doldurulur.
  //
  // KİLİT YOK — ve bu bilinçlidir (BGVL-137888 taramasında tekrar değerlendirildi, 25.08.2026).
  // Bildirim uçları anayasanın "kişisel kapsam" erişim grubundadır: kimlik doğrulaması ister ama
  // GRANT İSTEMEZ (bu yüzden /api/v1/management/* altında değiller ve NotificationController'da
  // @PreAuthorize yoktur; precedent GET /api/v1/me). Her kayıt oturum siciline bağlıdır ve daraltma
  // sorgunun kendisindedir. Buraya bir grant kilidi koymak, ekranı TÜM kullanıcılara kapatırdı —
  // yani düzeltme değil, gerileme olurdu.
  '/notifications': <NotificationsPage />,
}

/**
 * **Taşınan yollar** (2026-09-16 sol menü yeniden düzeni): adresler menüdeki yeni yerlerini yansıtacak
 * şekilde değişti — "Parametreler" grubu Admin modülüne geçtiği için `/minutes/*` → `/admin/parameters/*`,
 * Termin Güncelleme Talebi ise Görevler grubuna girdiği için grup kardeşleriyle aynı `/minutes/actions/*`
 * önekini aldı. Eski adresler yer imlerinde ve kullanıcı alışkanlığında duruyor — kaldırılsalardı 404'e
 * düşerlerdi; bu yüzden kalıcı yönlendirme olarak korunurlar. `replace` ile yönlendirilir, yoksa geri
 * tuşu kullanıcıyı eski adrese geri fırlatır ve döngü hissi verirdi.
 *
 * <p><b>Yönlendirme bir kapı DEĞİLDİR</b>: yetki kilidi hedef rotadadır (`withMenuGrant` → menü
 * `viewGrants`). Buraya ayrıca kilit koymak ikinci bir yetki listesi doğurur ve menüden sessizce
 * ayrışabilirdi.
 *
 * <p>Not: yeni `/minutes/actions/*` yolları `/minutes/actions/:id` detay rotasıyla aynı seviyededir;
 * React Router statik segmenti dinamikten önce sıraladığı için çakışma yoktur (dört kapsam alt sayfası
 * — all/created/assigned/followed — zaten bu şekilde yan yana duruyor).
 */
export const MOVED: Record<string, string> = {
  '/minutes/meeting-groups': '/admin/parameters/meeting-groups',
  '/minutes/meeting-decision-types': '/admin/parameters/meeting-decision-types',
  '/minutes/prefixes': '/admin/parameters/prefixes',
  '/minutes/deadline-requests': '/minutes/actions/deadline-requests',
  // ALT AĞACIYLA taşındı — bu ikisinde yönlendirmenin alt yolu koruması ŞART:
  // `/minutes/meetings/{id}`, `/group/{id}` ve `/drafts` hâlâ gönderilmiş e-postaların içinde
  // duruyor (bildirim şablonları toplantı detayına bu adresle bağlanıyor; 2026-09-16'da canlı
  // REF_NOTIFICATION_TEMPLATE üzerinde doğrulandı). Tam-yol eşleşen bir yönlendirme yalnız listeyi
  // kurtarır, e-postalardaki derin bağlantılar 404'e düşerdi.
  '/minutes/meetings': '/minutes/meeting-minutes/meetings',
  '/minutes/create': '/minutes/meeting-minutes/create',
}

/**
 * Eski önekten yenisine yönlendirir; **alt yolu, query'yi ve hash'i olduğu gibi taşır**
 * (`/minutes/meetings/42?tab=x` → `/minutes/meeting-minutes/meetings/42?tab=x`). Rota `/*` ile
 * kaydedildiğinden önekin kendisi de (alt yol boşken) eşleşir.
 */
function MovedPrefix({ to }: Readonly<{ to: string }>) {
  const rest = useParams()['*']
  const { search, hash, state } = useLocation()
  // `state` de taşınır: bugün eski adrese state ile giden bir çağrı yok, ama eklendiği gün
  // sessizce düşerdi (ör. originNav'ın "geri dönülecek konum"u) ve kaybı fark edilmezdi.
  return <Navigate to={`${to}${rest ? `/${rest}` : ''}${search}${hash}`} state={state} replace />
}

/**
 * Toplantı okuma yüzeylerinin yetki kümesi. **Menüden türetilir, burada tekrar YAZILMAZ** —
 * `menuGuard`'ın ilkesi budur ve elle kopyalanan bir liste menü öğesinden sessizce ayrışabilirdi.
 * `/minutes/meeting-minutes/meetings` menü öğesi zaten `MeetingController`/`MeetingGroupController`'daki
 * {@code HAS_MEETING_GROUP_READ} bileşiminin karşılığını taşır (görüntüleme VEYA yönetim); alt
 * rotaların ondan DAR kalmaması şart — dar kalsaydı yalnız yönetim yetkisi olan kullanıcı listeyi
 * görüp detayına giremezdi (BGVL-137394'ün ilk turunda tam bu hata yapılmıştı).
 */
export const MEETING_READ_GRANTS = MENU_VIEW_GRANTS['/minutes/meeting-minutes/meetings']

// Sol menüde olmayan ama erişilebilir rotalar: Talep Detay menüden kaldırıldı,
// ekrana yalnız Talep Listesi'ndeki "İncele" ile ulaşılır (?id= ile).
//
// MENÜDE OLMAMAK KİLİT DEĞİLDİR (THY sızma testi BGVL-137888): menü öğesi kaldırıldığı için bu ekran
// uzun süre kilitsiz kaldı ve adresi yazan ya da Yeni Talep sonrası yönlendirilen kullanıcı onu
// açabildi. `withMenuGrant` bu rotaları kapsayamaz — kilit menüden türetiliyor, bunların menü kaydı
// yok. Bu yüzden kilit BURADA, rota başına açıkça yazılır ve yetki kümesi ucun kendi korumasıyla
// BİREBİR aynı olmalıdır (aksi halde ya meşru kullanıcı kilitlenir ya boşluk kalır).
//
// Kilit satır içi `RequireGrant` ile de yazılabilirdi; bilinçli olarak BU HARİTADAN geçiriliyor:
// haritanın tipi `grants` alanını ZORUNLU kılar, yani yetkisiz bir menü-dışı rota eklemek
// derlenmez. Satır içi sarmalayıcıda o garanti yoktur — biri sarmalayıcıyı kaldırsa derleyici
// susardı ve bulgunun raporladığı durum aynen geri gelirdi (GitLab Duo bulgusu, MR !179).
export const OFF_MENU: Record<
  string,
  { element: ReactElement; grants: [GrantCode, ...GrantCode[]] }
> = {
  // Etkinlik Yönetici Özeti: ekrana Etkinlik listesindeki "İncele" ile gelinir.
  // ActivityController `GET /{id}` → HAS_ACTIVITY_VIEW.
  '/events/executive-summary': {
    element: <ExecutiveSummaryPage />,
    grants: [GRANTS.ACTIVITY_VIEW],
  },
  // Notlarım liste detayı (113): ekrana ana ekrandaki liste kartına tıklayarak gelinir, menüde yer
  // almaz. Yetki menüdekiyle aynıdır; hangi listeyi görebileceği ise KAYIT DÜZEYİNDE sunucuda
  // belirlenir (göremediği liste 404 döner), yani bu kilit yalnız modüle erişimi kontrol eder.
  '/minutes/todo-lists/:listId': {
    element: <TodoListDetailPage />,
    grants: [GRANTS.TODO_LIST_VIEW],
  },
  // TicketRequestController `GET /{id}` → HAS_TICKET_REQUEST_VIEW_ANY (üç okuma yetkisinin birleşimi).
  '/tickets/detail': {
    element: <TicketRequestDetailPage />,
    grants: [
      GRANTS.TICKET_REQUEST_VIEW,
      GRANTS.TICKET_REQUEST_VIEW_OWN,
      GRANTS.TICKET_REQUEST_VIEW_OWN_OR_IDENTIFIER,
    ],
  },
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* Public: OIDC giriş callback'i — auth-guard'ın DIŞINDA (token değişimi burada işlenir). */}
      <Route path="/auth/callback" element={<CallbackPage />} />
      {/* Korumalı: AppLayout altındaki tüm rotalar auth-guard ile korunur (FR-014). */}
      <Route
        element={
          <RequireAuth>
            <AccessGate>
              {/* Sıra bilinçli: İK erişim kapısı ÖNCE geçer, persona varsayılanı sonra seçilir. */}
              <AssistantPersonaBootstrap>
                <AppLayout />
              </AssistantPersonaBootstrap>
            </AccessGate>
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        {Object.entries(IMPLEMENTED).map(([path, element]) => (
          <Route key={path} path={path} element={withMenuGrant(path, element)} />
        ))}
        {/* Taşınan eski adresler → yeni adres (bkz. MOVED); kilit hedef rotadadır.
            `/*` ile kaydedilir: hem önekin kendisi hem de altındaki her yol yakalanır. */}
        {Object.entries(MOVED).map(([from, to]) => (
          <Route key={from} path={`${from}/*`} element={<MovedPrefix to={to} />} />
        ))}
        {MODULE_ITEMS.filter(
          (item): item is typeof item & { path: string } =>
            !!item.path && !(item.path in IMPLEMENTED),
        ).map((item) => (
          <Route
            key={item.path}
            path={item.path}
            element={withMenuGrant(item.path, <PlaceholderPage />)}
          />
        ))}
        {/* Toplantı akışı (044): grup kartı → grup toplantıları → detay (menüde yok; breadcrumb ile gezinir). */}
        <Route
          path="/minutes/meeting-minutes/meetings/group/:groupId"
          element={
            <RequireGrant anyOf={MEETING_READ_GRANTS}>
              <GroupMeetingsPage />
            </RequireGrant>
          }
        />
        {/* Taslaklar kartı → tüm gruplardaki taslaklar (statik segment, /:id'den önce eşleşir). */}
        <Route
          path="/minutes/meeting-minutes/meetings/drafts"
          element={
            <RequireGrant anyOf={MEETING_READ_GRANTS}>
              <DraftMeetingsPage />
            </RequireGrant>
          }
        />
        <Route
          path="/minutes/meeting-minutes/meetings/:id"
          element={
            <RequireGrant anyOf={MEETING_READ_GRANTS}>
              <MeetingDetailPage />
            </RequireGrant>
          }
        />
        {/* Arşivli detay: aynı ekran, farklı rota → menüde "Arşivlenenler" seçili kalır. */}
        <Route
          path="/minutes/archived/meetings/:id"
          element={
            <RequireGrant anyOf={[GRANTS.MEETING_ARCHIVE_VIEW, ...MEETING_READ_GRANTS]}>
              <MeetingDetailPage />
            </RequireGrant>
          }
        />
        {/* Aksiyon detay (menüde yok; Tüm Aksiyonlar satırından gelinir — 044/7, stub). */}
        <Route
          path="/minutes/actions/:id"
          element={
            <RequireGrant anyOf={[GRANTS.ACTION_DETAIL_VIEW]}>
              <ActionDetailPage />
            </RequireGrant>
          }
        />
        {/* Alt aksiyon detay — AYRI sayfa (066): yalnız tablo alanları + statü. */}
        <Route
          path="/minutes/subactions/:id"
          element={
            <RequireGrant anyOf={[GRANTS.SUBACTION_PAGE_VIEW]}>
              <SubActionDetailPage />
            </RequireGrant>
          }
        />
        {/* Arşivli aksiyon detay: aynı ekran, farklı rota → menüde "Arşivlenenler" seçili kalır (read-only). */}
        <Route
          path="/minutes/archived/actions/:id"
          element={
            <RequireGrant anyOf={[GRANTS.ACTION_ARCHIVE_VIEW]}>
              <ActionDetailPage />
            </RequireGrant>
          }
        />
        {/* Ajanda detayı OFF_MENU'de değil ve bilinçli olarak KİLİTSİZ: takvim AGENDA_VIEW ister, ama
            yetkisi olmayan davetli de davet edildiği toplantıyı açıp yanıt vermelidir (15.10.2026).
            Görünürlük kayıt düzeyinde sunucuda (görmediği davet 404). */}
        <Route path="/minutes/agenda/:id" element={<AgendaDetailPage />} />
        {Object.entries(OFF_MENU).map(([path, { element, grants }]) => (
          <Route
            key={path}
            path={path}
            element={<RequireGrant anyOf={grants}>{element}</RequireGrant>}
          />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
